// components/ScraperDashboard/SupplierDetailModal.js
// Modal for drilling down into supplier data with raw vs classified comparison
import React, { useState, useMemo, useEffect } from 'react';
import {
  X, ChevronDown, ChevronRight, Database, FileText, AlertCircle,
  Check, Copy, ExternalLink, ArrowRight, Layers, Loader2
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './SupplierDetailModal.css';

// Static mapping — defined outside component to avoid useMemo invalidation
const RAW_FIELD_MAPPINGS = {
  // CaleProcure SCPRS headers (exact names from the grid)
  'Department Name': 'Department Name',
  'Purchase Document #': 'Document Number',
  'Associated POs': 'Associated POs',
  'First Item Title': 'Description',
  'Start Date': 'Date',
  'End Date': 'Date (End)',
  'Grand Total': 'Amount',
  'Supplier ID': 'Supplier ID',
  'Supplier Name': 'Supplier Name',
  'Certification Type': 'Certification Type',
  'Acquisition Type_Sub-Type': 'Acquisition Type',
  'Acquisition Method': 'Acquisition Method',
  'LPA Contract ID': 'LPA Contract ID',
  'Buyer Name': 'Buyer',
  'Buyer Email': 'Buyer Email',
  'Status': 'PO Status',
  'Version': 'Version',
  // Legacy/detail scraper field names
  'Business Unit': 'Department Code',
  'Merchandise Amount': 'Amount (Merchandise)',
  'Awarded Amount': 'Amount (Awarded)',
  'Item Description': 'Description',
  'Address': 'Address',
  'Supplier City': 'City',
  'Supplier State': 'State',
  'Supplier Postal': 'Postal Code',
  'UNSPSC': 'UNSPSC Code',
  'Acquisition Type': 'Acquisition Type',
  'Buyer Email ID': 'Buyer Email',
};

/**
 * SupplierDetailModal - Shows detailed supplier data with raw vs classified comparison
 *
 * @param {Object} props
 * @param {Object} props.supplier - Supplier data from API (includes metadata with raw data)
 * @param {Function} props.onClose - Callback to close the modal
 */
function SupplierDetailModal({ supplier, onClose }) {
  const [activeTab, setActiveTab] = useState('comparison');
  const [copiedField, setCopiedField] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch PO detail data if available
  useEffect(() => {
    if (!supplier?.document_number) return;

    setDetailLoading(true);
    apiClient.get(`/api/scraper/po/${supplier.document_number}/detail`)
      .then(data => setDetailData(data))
      .catch(() => setDetailData(null))
      .finally(() => setDetailLoading(false));
  }, [supplier?.document_number]);

  // Extract raw data from metadata — handle both old (_raw.cells+headers) and new (fieldMap) formats
  // (Hooks below must run unconditionally; the `!supplier` early return sits after them.)
  const rawData = useMemo(() => {
    const m = supplier?.metadata || {};

    // New format: metadata is a clean header→value map (e.g. { "Department Name": "CAL FIRE", run_id: "..." })
    // Check if metadata has CaleProcure header keys directly
    if (m['Department Name'] || m['Supplier Name'] || m['Purchase Document #']) {
      return m;
    }

    // Old format: _raw has cells[] and headers[] arrays — build a map
    if (m._raw?.cells && m._raw?.headers) {
      const map = {};
      m._raw.headers.forEach((h, i) => {
        if (h && m._raw.cells[i]) map[h] = m._raw.cells[i];
      });
      return map;
    }

    // Fallback: scraper-mapped field names (documentNumber, supplierName, etc.)
    return m;
  }, [supplier?.metadata]);

  // Map classified data to display format
  const classifiedData = useMemo(() => (supplier == null ? {} : {
    'Document Number': supplier.document_number,
    'Supplier ID': supplier.supplier_number,
    'Supplier Name': supplier.supplier_name,
    'Amount': supplier.amount ? `$${parseFloat(supplier.amount).toLocaleString()}` : null,
    'Date': supplier.date,
    'Description': supplier.description,
    'Department Code': supplier.department_code,
    'Department Name': supplier.department_name,
    'Acquisition Type': supplier.acquisition_type,
    'Acquisition Method': supplier.acquisition_method,
    'Location': supplier.location,
    'Small Business': supplier.small_business ? 'Yes' : 'No',
    'DVBE': supplier.disabled_veteran ? 'Yes' : 'No',
    'Buyer': supplier.buyer,
    'Buyer Email': supplier.buyer_email,
    'PO Status': supplier.po_status,
    'Version': supplier.version,
    'Date (End)': supplier.end_date,
    'Associated POs': supplier.associated_pos,
    'LPA Contract ID': supplier.lpa_contract_id,
    'Certification Type': supplier.certification_type,
  }), [supplier]);


  // Create comparison pairs
  const comparisonPairs = useMemo(() => {
    const pairs = [];
    const usedRawKeys = new Set();

    // Go through classified fields and find matching raw fields
    Object.entries(classifiedData).forEach(([classifiedKey, classifiedValue]) => {
      // Find the raw field that maps to this classified field
      let rawKey = null;
      let rawValue = null;

      for (const [rk, displayLabel] of Object.entries(RAW_FIELD_MAPPINGS)) {
        if (displayLabel === classifiedKey && rawData[rk] !== undefined) {
          rawKey = rk;
          rawValue = rawData[rk];
          usedRawKeys.add(rk);
          break;
        }
      }

      // Also check scraper-mapped field names
      const scraperFieldMap = {
        'Document Number': ['documentNumber', 'purchaseDoc'],
        'Supplier ID': ['supplierId', 'supplierNumber'],
        'Supplier Name': ['supplierName'],
        'Amount': ['amount'],
        'Date': ['date', 'fromDate', 'toDate', 'startDate'],
        'Description': ['description'],
        'Department Code': ['department', 'businessUnit'],
        'Department Name': ['departmentName'],
        'Acquisition Type': ['acquisitionType', 'acqType'],
        'Acquisition Method': ['acquisitionMethod', 'acqMethod'],
        'Location': ['location'],
      };

      if (!rawKey && scraperFieldMap[classifiedKey]) {
        for (const field of scraperFieldMap[classifiedKey]) {
          if (rawData[field] !== undefined) {
            rawKey = field;
            rawValue = rawData[field];
            usedRawKeys.add(field);
            break;
          }
        }
      }

      pairs.push({
        label: classifiedKey,
        rawKey,
        rawValue: rawValue ?? '--',
        classifiedValue: classifiedValue ?? '--',
        match: rawValue && classifiedValue && String(rawValue).trim() === String(classifiedValue).trim().replace(/^\$/, '').replace(/,/g, ''),
        transformed: rawValue && classifiedValue && String(rawValue).trim() !== String(classifiedValue).trim() && classifiedValue !== '--',
      });
    });

    // Add any raw fields that weren't matched
    Object.entries(rawData).forEach(([key, value]) => {
      const INTERNAL_KEYS = ['_raw', '_unmapped', 'run_id', 'scrapedAt', 'certifications', 'contactInfo'];
      if (!usedRawKeys.has(key) && !INTERNAL_KEYS.includes(key)) {
        const displayLabel = RAW_FIELD_MAPPINGS[key] || key;
        pairs.push({
          label: displayLabel,
          rawKey: key,
          rawValue: value ?? '--',
          classifiedValue: '--',
          match: false,
          unmapped: true,
        });
      }
    });

    return pairs;
  }, [classifiedData, rawData]);

  if (!supplier) return null;

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const copyComparison = () => {
    const lines = [
      `Supplier Data Detail — ${supplier.document_number}`,
      `${'─'.repeat(60)}`,
      `${'Field'.padEnd(22)} ${'Raw Scraped'.padEnd(25)} ${'DB Value'.padEnd(25)} Status`,
      `${'─'.repeat(60)}`,
    ];
    comparisonPairs.forEach(p => {
      const status = p.match ? 'Match' : p.unmapped ? 'Unmapped' : (p.classifiedValue === '--' ? 'Not Stored' : 'Transformed');
      lines.push(`${String(p.label).padEnd(22)} ${String(p.rawValue).substring(0, 24).padEnd(25)} ${String(p.classifiedValue).substring(0, 24).padEnd(25)} ${status}`);
    });
    lines.push(`${'─'.repeat(60)}`);
    lines.push(`Record ID: ${supplier.id}`);
    navigator.clipboard.writeText(lines.join('\n'));
    setCopiedField('__all__');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatValue = (val) => {
    if (val === null || val === undefined) return '--';
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
  };

  return (
    <div className="sdm-overlay" onClick={onClose}>
      <div className="sdm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sdm-header">
          <div className="sdm-title">
            <h2>Supplier Data Detail</h2>
            <span className="sdm-doc-id">{supplier.document_number || 'No Document #'}</span>
            {supplier.detail_status && (
              <span className={`sdm-detail-status sdm-detail-status-${supplier.detail_status}`}>
                {supplier.detail_status === 'complete' ? 'Detail Scraped'
                  : supplier.detail_status === 'queued' || supplier.detail_status === 'processing' ? 'Detail Pending'
                  : supplier.detail_status === 'failed' ? 'Detail Failed'
                  : 'No Detail'}
              </span>
            )}
          </div>
          <button className="sdm-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="sdm-tabs">
          <button
            className={`sdm-tab ${activeTab === 'comparison' ? 'sdm-tab-active' : ''}`}
            onClick={() => setActiveTab('comparison')}
          >
            <Database size={14} />
            Side-by-Side Comparison
          </button>
          <button
            className={`sdm-tab ${activeTab === 'raw' ? 'sdm-tab-active' : ''}`}
            onClick={() => setActiveTab('raw')}
          >
            <FileText size={14} />
            Raw Scraped Data
          </button>
          <button
            className={`sdm-tab ${activeTab === 'classified' ? 'sdm-tab-active' : ''}`}
            onClick={() => setActiveTab('classified')}
          >
            <Check size={14} />
            Classified Data
          </button>
          <button
            className={`sdm-tab ${activeTab === 'lineItems' ? 'sdm-tab-active' : ''}`}
            onClick={() => setActiveTab('lineItems')}
            disabled={!detailData?.found}
          >
            <Layers size={14} />
            Line Items
            {detailData?.found && (
              <span className="sdm-tab-count">{detailData.lineItems?.length || 0}</span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="sdm-content">
          {activeTab === 'comparison' && (
            <div className="sdm-comparison">
              <div className="sdm-comparison-actions">
                <button className="sdm-copy-all" onClick={copyComparison}>
                  <Copy size={12} />
                  {copiedField === '__all__' ? 'Copied!' : 'Copy Comparison'}
                </button>
              </div>
              <div className="sdm-comparison-header">
                <div className="sdm-col-label">Field</div>
                <div className="sdm-col-raw">Raw Scraped</div>
                <div className="sdm-col-arrow"></div>
                <div className="sdm-col-classified">Classified / DB</div>
                <div className="sdm-col-status">Status</div>
              </div>
              <div className="sdm-comparison-body">
                {comparisonPairs.map((pair, i) => (
                  <div
                    key={i}
                    className={`sdm-row ${pair.match ? 'sdm-row-match' : ''} ${pair.transformed ? 'sdm-row-transformed' : ''} ${pair.unmapped ? 'sdm-row-unmapped' : ''}`}
                  >
                    <div className="sdm-col-label">
                      <span className="sdm-field-name">{pair.label}</span>
                      {pair.rawKey && pair.rawKey !== pair.label && (
                        <span className="sdm-raw-key">{pair.rawKey}</span>
                      )}
                    </div>
                    <div className="sdm-col-raw">
                      <span className="sdm-value" title={formatValue(pair.rawValue)}>
                        {formatValue(pair.rawValue)}
                      </span>
                      {pair.rawValue && pair.rawValue !== '--' && (
                        <button
                          className="sdm-copy"
                          onClick={() => copyToClipboard(formatValue(pair.rawValue), `raw-${i}`)}
                        >
                          {copiedField === `raw-${i}` ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                      )}
                    </div>
                    <div className="sdm-col-arrow">
                      <ArrowRight size={14} className="sdm-arrow" />
                    </div>
                    <div className="sdm-col-classified">
                      <span className="sdm-value" title={formatValue(pair.classifiedValue)}>
                        {formatValue(pair.classifiedValue)}
                      </span>
                      {pair.classifiedValue && pair.classifiedValue !== '--' && (
                        <button
                          className="sdm-copy"
                          onClick={() => copyToClipboard(formatValue(pair.classifiedValue), `class-${i}`)}
                        >
                          {copiedField === `class-${i}` ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                      )}
                    </div>
                    <div className="sdm-col-status">
                      {pair.match && (
                        <span className="sdm-status sdm-status-match">
                          <Check size={12} /> Match
                        </span>
                      )}
                      {pair.transformed && !pair.match && (
                        <span className="sdm-status sdm-status-transformed">
                          Transformed
                        </span>
                      )}
                      {pair.unmapped && (
                        <span className="sdm-status sdm-status-unmapped">
                          <AlertCircle size={12} /> Unmapped
                        </span>
                      )}
                      {!pair.match && !pair.transformed && !pair.unmapped && pair.rawValue !== '--' && pair.classifiedValue === '--' && (
                        <span className="sdm-status sdm-status-lost">
                          Not Stored
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'raw' && (
            <div className="sdm-raw-view">
              <div className="sdm-section-header">
                <span>Raw Scraped Data from Source</span>
                <button
                  className="sdm-copy-all"
                  onClick={() => copyToClipboard(JSON.stringify(rawData, null, 2), 'raw-all')}
                >
                  {copiedField === 'raw-all' ? <Check size={14} /> : <Copy size={14} />}
                  Copy JSON
                </button>
              </div>
              <pre className="sdm-json">
                {JSON.stringify(rawData, null, 2)}
              </pre>
            </div>
          )}

          {activeTab === 'classified' && (
            <div className="sdm-classified-view">
              <div className="sdm-section-header">
                <span>Data as Stored in Database</span>
                <button
                  className="sdm-copy-all"
                  onClick={() => copyToClipboard(JSON.stringify(supplier, null, 2), 'class-all')}
                >
                  {copiedField === 'class-all' ? <Check size={14} /> : <Copy size={14} />}
                  Copy JSON
                </button>
              </div>
              <div className="sdm-fields-grid">
                {Object.entries(classifiedData).map(([key, value]) => (
                  <div key={key} className="sdm-field-card">
                    <div className="sdm-field-label">{key}</div>
                    <div className="sdm-field-value">
                      {formatValue(value)}
                    </div>
                  </div>
                ))}
              </div>
              {supplier.metadata && (
                <div className="sdm-metadata-section">
                  <h4>Full Record (including metadata)</h4>
                  <pre className="sdm-json">
                    {JSON.stringify(supplier, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {activeTab === 'lineItems' && (
            <div className="sdm-line-items">
              {detailLoading && (
                <div className="sdm-loading">
                  <Loader2 size={20} className="sdm-spinner" />
                  Loading detail data...
                </div>
              )}
              {!detailLoading && !detailData?.found && (
                <div className="sdm-empty">
                  <AlertCircle size={20} />
                  <p>No detail data available for this PO.</p>
                  <p className="sdm-empty-hint">Detail extraction has not been run for this document.</p>
                </div>
              )}
              {!detailLoading && detailData?.found && (
                <>
                  {/* PO Header from Detail Scrape */}
                  <div className="sdm-section-header">
                    <span>PO Header (from Detail Page)</span>
                  </div>
                  <div className="sdm-detail-header-fields">
                    {detailData.header && Object.entries({
                      'Document #': detailData.header.purchase_document_number,
                      'Department': detailData.header.department_name || detailData.header.department_code,
                      'Supplier': detailData.header.supplier_name,
                      'Grand Total': detailData.header.grand_total ? `$${parseFloat(detailData.header.grand_total).toLocaleString()}` : '--',
                      'Merchandise': detailData.header.merchandise_amount ? `$${parseFloat(detailData.header.merchandise_amount).toLocaleString()}` : '--',
                      'Freight/Tax/Misc': detailData.header.freight_tax_misc ? `$${parseFloat(detailData.header.freight_tax_misc).toLocaleString()}` : '--',
                      'Status': detailData.header.transaction_status || '--',
                      'Start Date': detailData.header.start_date || '--',
                      'End Date': detailData.header.end_date || '--',
                      'Buyer': detailData.header.buyer_name || '--',
                      'Buyer Email': detailData.header.buyer_email || '--',
                      'Acquisition Method': detailData.header.acquisition_method || '--',
                      'LPA Contract': detailData.header.lpa_contract_id || '--',
                    }).filter(([_, v]) => v && v !== '--').map(([key, value]) => (
                      <div key={key} className="sdm-field-card">
                        <div className="sdm-field-label">{key}</div>
                        <div className="sdm-field-value">{value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Line Items Table */}
                  <div className="sdm-section-header" style={{ marginTop: '1rem' }}>
                    <span>Line Items ({detailData.lineItems?.length || 0})</span>
                  </div>
                  {detailData.lineItems?.length > 0 ? (
                    <div className="sdm-lines-table-wrap">
                      <table className="sdm-lines-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Description</th>
                            <th>UNSPSC</th>
                            <th>Qty</th>
                            <th>Unit Price</th>
                            <th>Line Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailData.lineItems.map((line, i) => (
                            <tr key={line.line_number || i}>
                              <td>{line.line_number || i + 1}</td>
                              <td className="sdm-line-desc">{line.item_description || '--'}</td>
                              <td className="sdm-line-code">{line.unspsc_code || '--'}</td>
                              <td>{line.quantity || '--'}</td>
                              <td>{line.unit_price ? `$${parseFloat(line.unit_price).toLocaleString()}` : '--'}</td>
                              <td>{line.line_total ? `$${parseFloat(line.line_total).toLocaleString()}` : '--'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="sdm-empty-lines">No line items found in detail extraction.</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sdm-footer">
          <span className="sdm-id">Record ID: {supplier.id || 'N/A'}</span>
          {supplier.inserted_at && (
            <span className="sdm-timestamp">
              Inserted: {new Date(supplier.inserted_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupplierDetailModal;
